import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faTrash } from "@fortawesome/pro-regular-svg-icons";

import { InputField } from "@/components/InputField/InputField";
import { SelectField } from "@/components/SelectField/SelectField";
import { Icon } from "@/components/Icon/Icon";

import { MessageLimitEntry } from "../../../schema/copywritingSettings";

const MESSAGE_TYPE_OPTIONS = [
	{ value: "li_connection_request", title: "LI Connection Request" },
	{ value: "li_introduction", title: "LI Introduction" },
	{ value: "li_follow_up", title: "LI Follow-up" },
	{ value: "li_breakup", title: "LI Breakup" },
	{ value: "email_introduction", title: "Email Introduction" },
	{ value: "email_follow_up", title: "Email Follow-up" },
	{ value: "email_breakup", title: "Email Breakup" },
];

const emptyEntry: MessageLimitEntry = {
	messageType: "li_connection_request",
	maxChars: null,
	minWords: null,
	maxWords: null,
};

interface MessageLimitsSectionProps {
	value: MessageLimitEntry[];
	onChange: (value: MessageLimitEntry[]) => void;
}

export const MessageLimitsSection: FunctionComponent<MessageLimitsSectionProps> = ({
	value,
	onChange,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`copywritingSettings.messageLimits.${key}`);

	const updateEntry = (index: number, field: keyof MessageLimitEntry, val: string) => {
		const updated = [...value];
		const numValue = val === "" ? null : Number(val);

		if (field === "messageType") {
			updated[index] = { ...updated[index], messageType: val };
		} else {
			updated[index] = { ...updated[index], [field]: numValue };
		}

		onChange(updated);
	};

	return (
		<div className="flex flex-col gap-3">
			<p className="text-sm text-text-meta">{ts("description")}</p>

			{value.length > 0 && (
				<div className="flex items-center gap-3 text-xs font-medium text-text-meta uppercase tracking-wide">
					<div className="w-48">{ts("messageType")}</div>
					<div className="w-28">{ts("maxChars")}</div>
					<div className="w-28">{ts("minWords")}</div>
					<div className="w-28">{ts("maxWords")}</div>
					<div className="w-8" />
				</div>
			)}

			{value.map((entry, index) => (
				<div key={index} className="flex items-start gap-3">
					<div className="w-48">
						<SelectField
							handleSelect={(v) => updateEntry(index, "messageType", v as string)}
							name={`msgType-${index}`}
							options={MESSAGE_TYPE_OPTIONS}
							placeholder={ts("messageType")}
							selectedOption={entry.messageType}
						/>
					</div>
					<div className="w-28">
						<InputField
							handleChange={(e) => updateEntry(index, "maxChars", e.target.value)}
							name={`maxChars-${index}`}
							placeholder={ts("maxChars")}
							showError={false}
							type="number"
							value={entry.maxChars ?? ""}
						/>
					</div>
					<div className="w-28">
						<InputField
							handleChange={(e) => updateEntry(index, "minWords", e.target.value)}
							name={`minWords-${index}`}
							placeholder={ts("minWords")}
							showError={false}
							type="number"
							value={entry.minWords ?? ""}
						/>
					</div>
					<div className="w-28">
						<InputField
							handleChange={(e) => updateEntry(index, "maxWords", e.target.value)}
							name={`maxWords-${index}`}
							placeholder={ts("maxWords")}
							showError={false}
							type="number"
							value={entry.maxWords ?? ""}
						/>
					</div>
					<button
						className="mt-2 text-text-meta hover:text-status-error"
						type="button"
						onClick={() => onChange(value.filter((_, i) => i !== index))}
					>
						<Icon className="w-4 h-4" icon={faTrash} />
					</button>
				</div>
			))}

			<button
				className="flex items-center gap-1 text-sm text-blue-action hover:underline self-start"
				type="button"
				onClick={() => onChange([...value, { ...emptyEntry }])}
			>
				<Icon className="w-3 h-3" icon={faPlus} />
				{ts("addRow")}
			</button>
		</div>
	);
};
